import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Grid,
} from "@mui/material";
import { createLead, submitInquiry, type LeadSource } from "./leadApi";

const NAVY = "#0d1b2a";
const GOLD = "#c9a96e";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultSource?: LeadSource;
}

const EVENT_TYPES = [
  "wedding", "corporate", "birthday", "engagement", "mehndi",
  "charity_gala", "convocation", "baby_shower", "anniversary",
  "retirement", "holiday_party", "other",
];

export default function CreateLeadDialog({ open, onClose, onCreated, defaultSource = "manual" }: Props) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    event_type: "",
    event_date: "",
    guest_count: "",
    budget_value: "",
    preferred_hall: "",
    message: "",
    source: defaultSource,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload: any = {
        ...form,
        guest_count: form.guest_count ? parseInt(form.guest_count) : null,
        budget_value: form.budget_value ? parseFloat(form.budget_value) : null,
      };
      if (form.source === "website") {
        await submitInquiry(payload);
      } else {
        await createLead(payload);
      }
      setForm({
        name: "", email: "", phone: "", organization: "",
        event_type: "", event_date: "", guest_count: "",
        budget_value: "", preferred_hall: "", message: "",
        source: defaultSource,
      });
      onCreated();
    } catch {}
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: NAVY, color: "#fff", fontWeight: 700 }}>
        New Lead
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Name *" size="small" fullWidth value={form.name} onChange={handleChange("name")} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Email" size="small" fullWidth value={form.email} onChange={handleChange("email")} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Phone" size="small" fullWidth value={form.phone} onChange={handleChange("phone")} />
            </Grid>
          </Grid>

          <TextField label="Organization" size="small" fullWidth value={form.organization} onChange={handleChange("organization")} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select value={form.event_type} label="Event Type" onChange={(e) => setForm((p) => ({ ...p, event_type: e.target.value }))}>
                  {EVENT_TYPES.map((t) => (
                    <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>
                      {t.replace(/_/g, " ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Event Date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.event_date}
                onChange={handleChange("event_date")}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Guest Count" type="number" size="small" fullWidth value={form.guest_count} onChange={handleChange("guest_count")} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Budget ($)" type="number" size="small" fullWidth value={form.budget_value} onChange={handleChange("budget_value")} />
            </Grid>
            <Grid item xs={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Source</InputLabel>
                <Select value={form.source} label="Source" onChange={(e) => setForm((p) => ({ ...p, source: e.target.value as LeadSource }))}>
                  <MenuItem value="website">Website</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="walk_in">Walk-in</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TextField
            label="Message / Notes"
            multiline
            rows={3}
            size="small"
            fullWidth
            value={form.message}
            onChange={handleChange("message")}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !form.name.trim()}
          sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1b2e44" } }}
        >
          {saving ? "Creating..." : "Create Lead"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
